import { useState, useEffect, useCallback, startTransition } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { telegramApi } from "@/services/api-v2.service";
import { Users, AlertTriangle, Settings, Plus, Edit, Trash2, MapPin, Clock, Lock, Unlock } from "lucide-react";
import type { BotSetting, IncidentType, IncidentTypeCreate, IncidentTypeUpdate, TelegramUser, TelegramUserCreate, TelegramUserUpdate, TelegramIncident, TelegramIncidentUpdate } from "@/types/api-v2";

// Расширенные интерфейсы для совместимости с существующим кодом
interface BotSettings extends BotSetting {
  botEnabled?: boolean;
  incidentReportEnabled?: boolean;
  locationSharingEnabled?: boolean;
  autoForwardToMap?: boolean;
  moderationEnabled?: boolean;
  showIncidentsOnMap?: boolean;
  autoApproveIncidents?: boolean;
  requireLocationForIncidents?: boolean;
  notifyAdminsOnIncident?: boolean;
}

interface ExtendedTelegramIncident extends TelegramIncident {
  latitude?: number;
  longitude?: number;
  photos?: string[] | null;
  severity?: string;
  userFirstName?: string | null;
  userLastName?: string | null;
  userUsername?: string | null;
  incidentTypeName?: string | null;
  incidentTypeIcon?: string | null;
  incidentTypeColor?: string | null;
}

export default function BotSettingsManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("settings");
  const [editingIncidentType, setEditingIncidentType] = useState<IncidentType | null>(null);
  const [showIncidentTypeDialog, setShowIncidentTypeDialog] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [selectedIncidentTypes, setSelectedIncidentTypes] = useState<number[]>([]);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Incident management state
  const [editingIncident, setEditingIncident] = useState<ExtendedTelegramIncident | null>(null);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [incidentDeleteConfirmId, setIncidentDeleteConfirmId] = useState<number | null>(null);
  const [selectedIncidents, setSelectedIncidents] = useState<number[]>([]);
  const [showIncidentBulkDeleteConfirm, setShowIncidentBulkDeleteConfirm] = useState(false);
  
  // User management state
  const [editingUser, setEditingUser] = useState<TelegramUser | null>(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [userDeleteConfirmId, setUserDeleteConfirmId] = useState<number | null>(null);
  
  // Form state for switches
  const [formState, setFormState] = useState({
    botEnabled: false,
    incidentReportEnabled: false,
    locationSharingEnabled: false,
    autoForwardToMap: false,
    moderationEnabled: false,
    showIncidentsOnMap: false,
    autoApproveIncidents: false,
    requireLocationForIncidents: false,
    notifyAdminsOnIncident: false,
  });

  // Функция для безопасного обновления состояния (избегает предупреждений React)
  const updateFormState = useCallback((updater: (prev: typeof formState) => typeof formState) => {
    // Используем startTransition для асинхронного обновления состояния
    // Это помечает обновление как не срочное и позволяет React обработать его в следующем цикле
    startTransition(() => {
      setFormState(updater);
    });
  }, []);

  // Bot Settings Query - получаем первую настройку или создаем дефолтную
  const { data: botSettingsList = [], isLoading: settingsLoading } = useQuery<BotSetting[]>({
    queryKey: ["telegram", "bot-settings"],
    queryFn: () => telegramApi.botSettings.list(),
  });

  const botSettings = botSettingsList[0] as BotSettings | undefined;

  // Initialize form state when bot settings are loaded (but not during mutation)
  const [isMutating, setIsMutating] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  
  useEffect(() => {
    if (botSettings && !isMutating && !isInitialized) {
      const settings = botSettings.settingValue || {};
      setFormState({
        botEnabled: settings.botEnabled ?? false,
        incidentReportEnabled: settings.incidentReportEnabled ?? false,
        locationSharingEnabled: settings.locationSharingEnabled ?? false,
        autoForwardToMap: settings.autoForwardToMap ?? false,
        moderationEnabled: settings.moderationEnabled ?? false,
        showIncidentsOnMap: settings.showIncidentsOnMap ?? false,
        autoApproveIncidents: settings.autoApproveIncidents ?? false,
        requireLocationForIncidents: settings.requireLocationForIncidents ?? false,
        notifyAdminsOnIncident: settings.notifyAdminsOnIncident ?? false,
      });
      setIsInitialized(true);
    } else if (!botSettings && isInitialized) {
      // Если настройки были удалены, сбрасываем инициализацию
      setIsInitialized(false);
    }
  }, [botSettings, isMutating, isInitialized]);

  // Incident Types Query
  const { data: incidentTypes = [], isLoading: typesLoading } = useQuery<IncidentType[]>({
    queryKey: ["telegram", "incident-types"],
    queryFn: () => telegramApi.incidentTypes.list(),
  });

  // Telegram Users Query
  const { data: telegramUsers = [], isLoading: usersLoading } = useQuery<TelegramUser[]>({
    queryKey: ["telegram", "users"],
    queryFn: () => telegramApi.users.list(),
  });

  // Telegram Incidents Query
  const { data: telegramIncidents = [], isLoading: incidentsLoading } = useQuery<TelegramIncident[]>({
    queryKey: ["telegram", "incidents"],
    queryFn: () => telegramApi.incidents.list(),
  });

  // Update Bot Settings Mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Partial<BotSettings>) => {
      setIsMutating(true);
      // Если настройки еще не созданы, создаем их
      if (!botSettings) {
        return await telegramApi.botSettings.create({
          settingKey: "main",
          settingValue: settings as any,
          description: "Основные настройки бота",
          category: "bot",
        });
      }
      // Иначе обновляем существующие
      return await telegramApi.botSettings.update(botSettings.settingKey, {
        settingValue: settings as any,
      });
    },
    onSuccess: (data) => {
      // Обновляем кеш с данными из ответа сервера
      queryClient.setQueryData(["telegram", "bot-settings"], (oldData: BotSetting[] | undefined) => {
        if (!oldData) {
          return [data];
        }
        // Ищем индекс существующей настройки
        const index = oldData.findIndex(s => s.settingKey === data.settingKey);
        if (index >= 0) {
          // Заменяем существующий элемент
          const newData = [...oldData];
          newData[index] = data;
          return newData;
        } else {
          // Добавляем новый элемент
          return [...oldData, data];
        }
      });
      
      // Обновляем локальное состояние из сохранённых данных
      if (data.settingValue) {
        setFormState({
          botEnabled: data.settingValue.botEnabled ?? false,
          incidentReportEnabled: data.settingValue.incidentReportEnabled ?? false,
          locationSharingEnabled: data.settingValue.locationSharingEnabled ?? false,
          autoForwardToMap: data.settingValue.autoForwardToMap ?? false,
          moderationEnabled: data.settingValue.moderationEnabled ?? false,
          showIncidentsOnMap: data.settingValue.showIncidentsOnMap ?? false,
          autoApproveIncidents: data.settingValue.autoApproveIncidents ?? false,
          requireLocationForIncidents: data.settingValue.requireLocationForIncidents ?? false,
          notifyAdminsOnIncident: data.settingValue.notifyAdminsOnIncident ?? false,
        });
        setIsInitialized(true);
      }
      
      toast({ title: "Настройки бота обновлены", description: "Изменения сохранены успешно" });
      
      // Сбрасываем флаг мутации через небольшую задержку
      setTimeout(() => setIsMutating(false), 100);
    },
    onError: (error) => {
      console.error("Settings update error:", error);
      toast({ title: "Ошибка", description: "Не удалось обновить настройки", variant: "destructive" });
      setIsMutating(false);
    },
  });

  // Create/Update Incident Type Mutation
  const saveIncidentTypeMutation = useMutation({
    mutationFn: async (type: Partial<IncidentType>) => {
      if (type.id) {
        return await telegramApi.incidentTypes.update(type.id, type as IncidentTypeUpdate);
      } else {
        return await telegramApi.incidentTypes.create(type as IncidentTypeCreate);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "incident-types"] });
      setShowIncidentTypeDialog(false);
      setEditingIncidentType(null);
      toast({ title: "Тип происшествия сохранен", description: "Изменения применены успешно" });
    },
    onError: (error) => {
      console.error("Mutation error:", error);
      toast({ title: "Ошибка", description: "Не удалось сохранить тип происшествия", variant: "destructive" });
    },
  });

  // Delete Incident Type Mutation
  const deleteIncidentTypeMutation = useMutation({
    mutationFn: async (id: number) => {
      await telegramApi.incidentTypes.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "incident-types"] });
      setDeleteConfirmId(null);
      toast({ title: "Тип происшествия удален", description: "Операция выполнена успешно" });
    },
    onError: () => {
      setDeleteConfirmId(null);
      toast({ title: "Ошибка", description: "Не удалось удалить тип происшествия", variant: "destructive" });
    },
  });

  const handleConfirmDelete = () => {
    if (deleteConfirmId) {
      deleteIncidentTypeMutation.mutate(deleteConfirmId);
    }
  };

  // Bulk Delete Incident Types Mutation
  const bulkDeleteIncidentTypesMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      await Promise.all(ids.map(id => telegramApi.incidentTypes.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "incident-types"] });
      setSelectedIncidentTypes([]);
      setShowBulkDeleteConfirm(false);
      toast({ title: "Типы происшествий удалены", description: `Удалено ${selectedIncidentTypes.length} записей` });
    },
    onError: () => {
      setShowBulkDeleteConfirm(false);
      toast({ title: "Ошибка", description: "Не удалось удалить выбранные типы происшествий", variant: "destructive" });
    },
  });

  const handleBulkDelete = () => {
    if (selectedIncidentTypes.length > 0) {
      bulkDeleteIncidentTypesMutation.mutate(selectedIncidentTypes);
    }
  };

  const handleSelectIncidentType = (id: number, checked: boolean) => {
    if (checked) {
      setSelectedIncidentTypes(prev => [...prev, id]);
    } else {
      setSelectedIncidentTypes(prev => prev.filter(typeId => typeId !== id));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIncidentTypes(incidentTypes.map(type => type.id));
    } else {
      setSelectedIncidentTypes([]);
    }
  };

  const isAllSelected = incidentTypes.length > 0 && selectedIncidentTypes.length === incidentTypes.length;
  const isPartiallySelected = selectedIncidentTypes.length > 0 && selectedIncidentTypes.length < incidentTypes.length;

  const handleSettingsSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    // Объединяем существующие настройки с новыми, чтобы не потерять данные
    const existingSettings = botSettings?.settingValue || {};
    
    const settings = {
      ...existingSettings,
      botEnabled: formState.botEnabled,
      botToken: (formData.get("botToken") as string) || existingSettings.botToken || "",
      botUsername: (formData.get("botUsername") as string) || existingSettings.botUsername || "",
      webhookUrl: (formData.get("webhookUrl") as string) || existingSettings.webhookUrl || "",
      // Четыре основные функции бота
      incidentReportEnabled: formState.incidentReportEnabled,
      locationSharingEnabled: formState.locationSharingEnabled,
      autoForwardToMap: formState.autoForwardToMap,
      moderationEnabled: formState.moderationEnabled,
      // Сообщения
      welcomeMessage: (formData.get("welcomeMessage") as string) || existingSettings.welcomeMessage || "",
      helpMessage: (formData.get("helpMessage") as string) || existingSettings.helpMessage || "",
      // Legacy настройки
      showIncidentsOnMap: formState.showIncidentsOnMap,
      autoApproveIncidents: formState.autoApproveIncidents,
      requireLocationForIncidents: formState.requireLocationForIncidents,
      notifyAdminsOnIncident: formState.notifyAdminsOnIncident,
      notificationChannelId: (formData.get("notificationChannelId") as string) || existingSettings.notificationChannelId || "",
    };

    console.log("Отправляемые настройки:", settings);
    console.log("Текущее состояние формы:", formState);
    updateSettingsMutation.mutate(settings);
  };

  const handleIncidentTypeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    
    const buttonText = formData.get("buttonText") as string;
    const icon = formData.get("iconEmoji") as string;
    
    const type = {
      id: editingIncidentType?.id,
      name: formData.get("name") as string,
      description: formData.get("description") as string || null,
      icon: icon || null,
      color: formData.get("color") as string || null,
      isActive: formData.get("isActive") === "on",
      priority: parseInt(formData.get("sortOrder") as string) || 0,
      metadata: buttonText ? { buttonText } : null,
    };

    saveIncidentTypeMutation.mutate(type);
  };

  // Incident management handlers
  const handleEditIncident = (incident: TelegramIncident) => {
    setEditingIncident(incident as ExtendedTelegramIncident);
    setSelectedStatus(incident.status);
    setShowIncidentDialog(true);
  };

  const handleDeleteIncident = (incidentId: number) => {
    setIncidentDeleteConfirmId(incidentId);
  };

  const confirmDeleteIncident = () => {
    if (incidentDeleteConfirmId) {
      deleteIncidentMutation.mutate(incidentDeleteConfirmId);
    }
  };

  const handleBulkDeleteIncidents = () => {
    if (selectedIncidents.length > 0) {
      setShowIncidentBulkDeleteConfirm(true);
    }
  };

  const confirmBulkDeleteIncidents = () => {
    bulkDeleteIncidentsMutation.mutate(selectedIncidents);
  };

  // Delete incident mutation
  const deleteIncidentMutation = useMutation({
    mutationFn: async (incidentId: number) => {
      await telegramApi.incidents.delete(incidentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "incidents"] });
      setIncidentDeleteConfirmId(null);
      toast({
        title: "Успешно",
        description: "Происшествие удалено",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить происшествие",
        variant: "destructive",
      });
    },
  });

  // Bulk delete incidents mutation
  const bulkDeleteIncidentsMutation = useMutation({
    mutationFn: async (incidentIds: number[]) => {
      // Удаляем инциденты по одному, так как bulk-delete может быть не реализован
      await Promise.all(incidentIds.map(id => telegramApi.incidents.delete(id)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "incidents"] });
      setSelectedIncidents([]);
      setShowIncidentBulkDeleteConfirm(false);
      toast({
        title: "Успешно",
        description: "Происшествия удалены",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить происшествия",
        variant: "destructive",
      });
    },
  });

  // Update incident mutation
  const updateIncidentMutation = useMutation({
    mutationFn: async (incident: Partial<TelegramIncident> & { id: number }) => {
      await telegramApi.incidents.update(incident.id, incident as TelegramIncidentUpdate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "incidents"] });
      setShowIncidentDialog(false);
      setEditingIncident(null);
      toast({
        title: "Успешно",
        description: "Происшествие обновлено",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить происшествие",
        variant: "destructive",
      });
    },
  });

  // User management mutations
  const createUserMutation = useMutation({
    mutationFn: async (userData: Partial<TelegramUser>) => {
      await telegramApi.users.create(userData as TelegramUserCreate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "users"] });
      setShowUserDialog(false);
      setEditingUser(null);
      toast({
        title: "Успешно",
        description: "Пользователь создан",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось создать пользователя",
        variant: "destructive",
      });
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: async (user: Partial<TelegramUser> & { id: number }) => {
      await telegramApi.users.update(user.id, user as TelegramUserUpdate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "users"] });
      setShowUserDialog(false);
      setEditingUser(null);
      toast({
        title: "Успешно",
        description: "Пользователь обновлен",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось обновить пользователя",
        variant: "destructive",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      await telegramApi.users.delete(userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "users"] });
      setUserDeleteConfirmId(null);
      toast({
        title: "Успешно",
        description: "Пользователь удален",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось удалить пользователя",
        variant: "destructive",
      });
    },
  });

  const toggleUserBlockMutation = useMutation({
    mutationFn: async ({ userId, isBlocked }: { userId: number; isBlocked: boolean }) => {
      await telegramApi.users.update(userId, { isActive: !isBlocked } as TelegramUserUpdate);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["telegram", "users"] });
      toast({
        title: "Успешно",
        description: "Статус пользователя изменен",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Ошибка",
        description: error.message || "Не удалось изменить статус пользователя",
        variant: "destructive",
      });
    },
  });

  // User handler functions
  const handleEditUser = (user: TelegramUser) => {
    setEditingUser(user);
    setShowUserDialog(true);
  };

  const handleToggleUserBlock = (user: TelegramUser) => {
    toggleUserBlockMutation.mutate({
      userId: user.id,
      isBlocked: !user.isBlocked,
    });
  };

  if (settingsLoading) {
    return <div className="flex items-center justify-center p-8">Загрузка настроек бота...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex space-x-1 bg-muted p-1 rounded-lg">
        <Button 
          variant={activeTab === "settings" ? "default" : "ghost"} 
          onClick={() => setActiveTab("settings")}
          className="flex-1"
        >
          <Settings className="h-4 w-4 mr-2" />
          Настройки
        </Button>
        <Button 
          variant={activeTab === "types" ? "default" : "ghost"} 
          onClick={() => setActiveTab("types")}
          className="flex-1"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Типы происшествий
        </Button>
        <Button 
          variant={activeTab === "users" ? "default" : "ghost"} 
          onClick={() => setActiveTab("users")}
          className="flex-1"
        >
          <Users className="h-4 w-4 mr-2" />
          Пользователи
        </Button>
        <Button 
          variant={activeTab === "incidents" ? "default" : "ghost"} 
          onClick={() => setActiveTab("incidents")}
          className="flex-1"
        >
          <MapPin className="h-4 w-4 mr-2" />
          Происшествия
        </Button>
      </div>

      {activeTab === "settings" && (
        <Card>
          <CardHeader>
            <CardTitle>Настройки Telegram бота</CardTitle>
            <CardDescription>
              Конфигурация бота для приема сообщений о происшествиях
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSettingsSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Switch 
                      name="botEnabled" 
                      checked={formState.botEnabled}
                      onCheckedChange={(checked) => updateFormState(prev => ({...prev, botEnabled: checked}))}
                    />
                    <Label htmlFor="botEnabled">Включить бота</Label>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="botToken">Токен бота</Label>
                    <Input
                      id="botToken"
                      name="botToken"
                      type="password"
                      defaultValue={botSettings?.settingValue?.botToken || ""}
                      placeholder="1234567890:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="botUsername">Имя пользователя бота</Label>
                    <Input
                      id="botUsername"
                      name="botUsername"
                      defaultValue={botSettings?.settingValue?.botUsername || ""}
                      placeholder="@incident_report_bot"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="webhookUrl">URL webhook</Label>
                    <Input
                      id="webhookUrl"
                      name="webhookUrl"
                      defaultValue={botSettings?.settingValue?.webhookUrl || ""}
                      placeholder="https://your-domain.com/api/telegram/webhook"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notificationChannelId">ID канала уведомлений</Label>
                    <Input
                      id="notificationChannelId"
                      name="notificationChannelId"
                      defaultValue={botSettings?.settingValue?.notificationChannelId || ""}
                      placeholder="-1001234567890"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="welcomeMessage">Приветственное сообщение</Label>
                    <Textarea
                      id="welcomeMessage"
                      name="welcomeMessage"
                      rows={3}
                      defaultValue={botSettings?.settingValue?.welcomeMessage || "Добро пожаловать в систему оповещения об инцидентах! 🚨\n\nЯ помогу вам сообщить о происшествиях на объектах инфраструктуры. Для начала отправьте команду /help или поделитесь геолокацией места инцидента."}
                      placeholder="Добро пожаловать! Отправьте геолокацию для сообщения о происшествии."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="helpMessage">Сообщение помощи</Label>
                    <Textarea
                      id="helpMessage"
                      name="helpMessage"
                      rows={3}
                      defaultValue={botSettings?.settingValue?.helpMessage || "📋 Инструкция по использованию:\n\n1. Отправьте геолокацию места происшествия\n2. Выберите тип инцидента из предложенных\n3. Опишите ситуацию и приложите фото\n4. Ваше сообщение будет передано диспетчеру\n\nКоманды:\n/start - начать работу\n/help - показать помощь\n/status - статус системы"}
                      placeholder="Инструкция по использованию бота..."
                    />
                  </div>

                  <Separator />

                  <div className="space-y-3">
                    <h4 className="font-medium">Функции бота</h4>
                    
                    <div className="flex items-center space-x-2">
                      <Switch 
                        name="incidentReportEnabled" 
                        checked={formState.incidentReportEnabled}
                        onCheckedChange={(checked) => updateFormState(prev => ({...prev, incidentReportEnabled: checked}))}
                      />
                      <Label>Прием сообщений о происшествиях</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch 
                        name="locationSharingEnabled" 
                        checked={formState.locationSharingEnabled}
                        onCheckedChange={(checked) => updateFormState(prev => ({...prev, locationSharingEnabled: checked}))}
                      />
                      <Label>Обмен геолокацией</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch 
                        name="autoForwardToMap" 
                        checked={formState.autoForwardToMap}
                        onCheckedChange={(checked) => updateFormState(prev => ({...prev, autoForwardToMap: checked}))}
                      />
                      <Label>Автоматическое отображение на карте</Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch 
                        name="moderationEnabled" 
                        checked={formState.moderationEnabled}
                        onCheckedChange={(checked) => updateFormState(prev => ({...prev, moderationEnabled: checked}))}
                      />
                      <Label>Модерация сообщений</Label>
                    </div>
                  </div>
                </div>
              </div>

              <Button type="submit" disabled={updateSettingsMutation.isPending}>
                {updateSettingsMutation.isPending ? "Сохранение..." : "Сохранить настройки"}
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {activeTab === "types" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Типы происшествий</CardTitle>
                <CardDescription>
                  Управление категориями происшествий для бота
                </CardDescription>
                {selectedIncidentTypes.length > 0 && (
                  <div className="text-sm text-blue-600 mt-1">
                    Выбрано: {selectedIncidentTypes.length} записей
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                {selectedIncidentTypes.length > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    disabled={bulkDeleteIncidentTypesMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Удалить выбранные ({selectedIncidentTypes.length})
                  </Button>
                )}
                <Dialog open={showIncidentTypeDialog} onOpenChange={setShowIncidentTypeDialog}>
                  <DialogTrigger asChild>
                    <Button onClick={() => { setEditingIncidentType(null); setShowIncidentTypeDialog(true); }}>
                      <Plus className="h-4 w-4 mr-2" />
                      Добавить тип
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>
                      {editingIncidentType ? "Редактировать тип" : "Добавить тип происшествия"}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleIncidentTypeSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Название</Label>
                      <Input
                        id="name"
                        name="name"
                        defaultValue={editingIncidentType?.name || ""}
                        placeholder="Пожар"
                        required
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="description">Описание</Label>
                      <Textarea
                        id="description"
                        name="description"
                        defaultValue={editingIncidentType?.description || ""}
                        placeholder="Описание типа происшествия"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="buttonText">Текст кнопки в боте</Label>
                      <Input
                        id="buttonText"
                        name="buttonText"
                        defaultValue={editingIncidentType?.metadata?.buttonText || (editingIncidentType?.name ? `${editingIncidentType.name} ${editingIncidentType.icon || ''}` : "")}
                        placeholder="Пожар 🔥"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="iconEmoji">Иконка</Label>
                        <Input
                          id="iconEmoji"
                          name="iconEmoji"
                          defaultValue={editingIncidentType?.icon || ""}
                          placeholder="🔥"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="color">Цвет</Label>
                        <Input
                          id="color"
                          name="color"
                          type="color"
                          defaultValue={editingIncidentType?.color || "#ff0000"}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="sortOrder">Порядок сортировки</Label>
                      <Input
                        id="sortOrder"
                        name="sortOrder"
                        type="number"
                        defaultValue={editingIncidentType?.priority || 0}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch 
                        name="isActive" 
                        defaultChecked={editingIncidentType?.isActive !== false}
                      />
                      <Label>Активен</Label>
                    </div>

                    <Button type="submit" disabled={saveIncidentTypeMutation.isPending}>
                      {saveIncidentTypeMutation.isPending ? "Сохранение..." : "Сохранить"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {typesLoading ? (
              <div className="text-center py-4">Загрузка типов происшествий...</div>
            ) : (
              <div className="border rounded-lg">
                <div className="sticky top-0 bg-background border-b z-10">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox
                            checked={isAllSelected}
                            onCheckedChange={handleSelectAll}
                            {...(isPartiallySelected && { 'data-state': 'indeterminate' })}
                          />
                        </TableHead>
                        <TableHead>Название</TableHead>
                        <TableHead>Текст кнопки</TableHead>
                        <TableHead>Статус</TableHead>
                        <TableHead>Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                  </Table>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableBody>
                      {incidentTypes.map((type) => (
                        <TableRow key={type.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedIncidentTypes.includes(type.id)}
                              onCheckedChange={(checked) => handleSelectIncidentType(type.id, !!checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              {type.icon && <span className="text-lg">{type.icon}</span>}
                              <div>
                                <div className="font-medium">{type.name}</div>
                                <div className="text-sm text-muted-foreground">{type.description}</div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-muted-foreground">{type.metadata?.buttonText || ''}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant={type.isActive ? "default" : "secondary"}>
                              {type.isActive ? "Активен" : "Неактивен"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingIncidentType(type);
                                  setShowIncidentTypeDialog(true);
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setDeleteConfirmId(type.id)}
                                disabled={deleteIncidentTypeMutation.isPending}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "users" && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Пользователи Telegram</CardTitle>
                <CardDescription>
                  Управление пользователями бота
                </CardDescription>
              </div>
              <Button onClick={() => setShowUserDialog(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Добавить пользователя
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {usersLoading ? (
              <div className="text-center py-4">Загрузка пользователей...</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>Происшествий</TableHead>
                    <TableHead>Последняя активность</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {telegramUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            @{user.username} (ID: {user.telegramId})
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isAdmin ? "default" : "secondary"}>
                          {user.isAdmin ? "Администратор" : "Пользователь"}
                        </Badge>
                      </TableCell>
                      <TableCell>{user.totalIncidents}</TableCell>
                      <TableCell>
                        {user.lastActivity ? (
                          <div className="flex items-center space-x-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm">
                              {new Date(user.lastActivity).toLocaleDateString()}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Никогда</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.isBlocked ? "destructive" : "default"}>
                          {user.isBlocked ? "Заблокирован" : "Активен"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUserBlock(user)}
                            className={user.isBlocked ? "text-green-600 hover:text-green-900" : "text-red-600 hover:text-red-900"}
                          >
                            {user.isBlocked ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setUserDeleteConfirmId(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "incidents" && (
        <Card>
          <CardHeader>
            <CardTitle>Происшествия из Telegram</CardTitle>
            <CardDescription>
              Обзор сообщений о происшествиях от пользователей бота
            </CardDescription>
          </CardHeader>
          <CardContent>
            {incidentsLoading ? (
              <div className="text-center py-4">Загрузка происшествий...</div>
            ) : (
              <div className="space-y-4">
                {selectedIncidents.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteIncidents}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Удалить выбранные ({selectedIncidents.length})
                    </Button>
                  </div>
                )}
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={selectedIncidents.length === telegramIncidents.length && telegramIncidents.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedIncidents(telegramIncidents.map(incident => incident.id));
                            } else {
                              setSelectedIncidents([]);
                            }
                          }}
                        />
                      </TableHead>
                      <TableHead>ID</TableHead>
                      <TableHead>Происшествие</TableHead>
                    <TableHead>Местоположение</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Серьезность</TableHead>
                    <TableHead>Дата</TableHead>
                    <TableHead>Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {telegramIncidents.map((incident) => (
                    <TableRow key={incident.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIncidents.includes(incident.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedIncidents([...selectedIncidents, incident.id]);
                            } else {
                              setSelectedIncidents(selectedIncidents.filter(id => id !== incident.id));
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">#{incident.id}</Badge>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{incident.incidentTypeName || 'Инцидент'}</div>
                          <div className="text-sm text-muted-foreground">
                            {incident.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {incident.latitude?.toFixed(4)}, {incident.longitude?.toFixed(4)}
                          </span>
                        </div>
                        {incident.address && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {incident.address}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          incident.status === "approved" ? "default" :
                          incident.status === "rejected" ? "destructive" : "secondary"
                        }>
                          {incident.status === "pending" ? "На рассмотрении" :
                           incident.status === "pending_review" ? "На модерации" :
                           incident.status === "approved" ? "Одобрено" :
                           incident.status === "rejected" ? "Отклонено" : incident.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          incident.severity === "critical" ? "destructive" :
                          incident.severity === "high" ? "default" :
                          incident.severity === "medium" ? "secondary" : "outline"
                        }>
                          {incident.severity === "low" ? "Низкая" :
                           incident.severity === "medium" ? "Средняя" :
                           incident.severity === "high" ? "Высокая" : "Критическая"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {new Date(incident.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditIncident(incident)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteIncident(incident.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteConfirm} onOpenChange={setShowBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Подтвердите массовое удаление</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить {selectedIncidentTypes.length} выбранных типов происшествий? 
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleteIncidentTypesMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleteIncidentTypesMutation.isPending ? "Удаление..." : `Удалить ${selectedIncidentTypes.length} записей`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirmation Dialog for Delete */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Подтвердите удаление</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить этот тип происшествия? Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deleteIncidentTypeMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteIncidentTypeMutation.isPending ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Incident Edit Dialog */}
      <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
        <DialogContent className="sm:max-w-[425px] overflow-visible">
          <DialogHeader>
            <DialogTitle>Редактировать происшествие</DialogTitle>
            <DialogDescription>
              Изменить статус и заметки администратора для происшествия.
            </DialogDescription>
          </DialogHeader>
          
          {editingIncident && (
            <>
              <div className="mb-2 text-sm text-gray-600">
                Редактирование происшествия ID: {editingIncident.id}, текущий статус: {selectedStatus}
              </div>
              <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              updateIncidentMutation.mutate({
                id: editingIncident.id,
                status: selectedStatus,
                adminNotes: formData.get("adminNotes") as string,
              });
            }}>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="incident-status">Статус</Label>
                  <select
                    id="incident-status"
                    value={selectedStatus}
                    onChange={(e) => {
                      console.log("Status changed to:", e.target.value);
                      setSelectedStatus(e.target.value);
                    }}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="pending">На рассмотрении</option>
                    <option value="pending_review">На модерации</option>
                    <option value="approved">Одобрено</option>
                    <option value="rejected">Отклонено</option>
                  </select>
                </div>
                
                <div className="grid gap-2">
                  <Label htmlFor="admin-notes">Заметки администратора</Label>
                  <Textarea
                    name="adminNotes"
                    defaultValue={editingIncident.adminNotes || ""}
                    placeholder="Комментарии или причина отклонения..."
                    rows={3}
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowIncidentDialog(false)}>
                  Отмена
                </Button>
                <Button type="submit" disabled={updateIncidentMutation.isPending}>
                  {updateIncidentMutation.isPending ? "Сохранение..." : "Сохранить"}
                </Button>
              </DialogFooter>
            </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Incident Delete Confirmation */}
      <AlertDialog open={incidentDeleteConfirmId !== null} onOpenChange={() => setIncidentDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить происшествие</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить это происшествие? Это действие необратимо.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteIncident}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteIncidentMutation.isPending ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Incident Bulk Delete Confirmation */}
      <AlertDialog open={showIncidentBulkDeleteConfirm} onOpenChange={setShowIncidentBulkDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить выбранные происшествия</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить {selectedIncidents.length} выбранных происшествий? Это действие необратимо.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmBulkDeleteIncidents}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkDeleteIncidentsMutation.isPending ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* User Create/Edit Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? "Редактировать пользователя" : "Добавить пользователя"}
            </DialogTitle>
            <DialogDescription>
              {editingUser ? "Изменить данные пользователя" : "Создать нового пользователя Telegram"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            const userData = {
              telegramId: Number(formData.get("telegramId")),
              username: formData.get("username") as string,
              firstName: formData.get("firstName") as string,
              lastName: formData.get("lastName") as string,
              isAdmin: formData.get("isAdmin") === "on",
              isBlocked: formData.get("isBlocked") === "on",
            };
            
            if (editingUser) {
              updateUserMutation.mutate({ ...userData, id: editingUser.id });
            } else {
              createUserMutation.mutate(userData);
            }
          }}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="telegramId">Telegram ID</Label>
                <Input
                  id="telegramId"
                  name="telegramId"
                  type="number"
                  defaultValue={editingUser?.telegramId || ""}
                  required
                  placeholder="Например: 123456789"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  name="username"
                  defaultValue={editingUser?.username || ""}
                  placeholder="@username (без @)"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="firstName">Имя</Label>
                <Input
                  id="firstName"
                  name="firstName"
                  defaultValue={editingUser?.firstName || ""}
                  placeholder="Имя пользователя"
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="lastName">Фамилия</Label>
                <Input
                  id="lastName"
                  name="lastName"
                  defaultValue={editingUser?.lastName || ""}
                  placeholder="Фамилия пользователя"
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isAdmin"
                  name="isAdmin"
                  defaultChecked={editingUser?.isAdmin || false}
                  className="rounded border border-input"
                />
                <Label htmlFor="isAdmin">Администратор</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isBlocked"
                  name="isBlocked"
                  defaultChecked={editingUser?.isBlocked || false}
                  className="rounded border border-input"
                />
                <Label htmlFor="isBlocked">Заблокирован</Label>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setShowUserDialog(false);
                setEditingUser(null);
              }}>
                Отмена
              </Button>
              <Button type="submit" disabled={createUserMutation.isPending || updateUserMutation.isPending}>
                {(createUserMutation.isPending || updateUserMutation.isPending) ? "Сохранение..." : "Сохранить"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Delete Confirmation */}
      <AlertDialog open={userDeleteConfirmId !== null} onOpenChange={() => setUserDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить пользователя</AlertDialogTitle>
            <AlertDialogDescription>
              Вы уверены, что хотите удалить этого пользователя? Это действие необратимо и удалит все связанные данные.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (userDeleteConfirmId) {
                  deleteUserMutation.mutate(userDeleteConfirmId);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteUserMutation.isPending ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}